import { ICloudForm } from '../../CalendarConnect';
import { useAlfredInOutreachEnabled } from '../../../lib/alfred-in-outreach-flag';
import { listedOnTools } from '../../../lib/outreach-alfred';
import { connectorDisplayName } from '../../../lib/connectors';
import { sortConnectors, type AppsController } from '../../../lib/use-connectors';
import { useLoadBi } from '../../../lib/use-load-bi';
import { useTranslation } from '../../../lib/i18n';
import { ConnectorCard } from './ConnectorCard';
import { comingSoonRow, RequestConnectorCard, useAskForConnector } from './ConnectorRequestCards';
import { CardGridSkeleton, EmptyCard, ErrorLine, Section } from './primitives';
import { matchesConnectorQuery } from './matching';
import styles from './connectors.module.css';

function connectorCard(
  c: ReturnType<typeof sortConnectors>[number],
  controller: AppsController,
  multiAccountEnabled: boolean,
) {
  const { busy, connect, disconnect, addAnother } = controller;
  return (
    <ConnectorCard
      key={c.toolkit}
      connector={c}
      busy={busy}
      multiAccountEnabled={multiAccountEnabled}
      onConnect={connect}
      onDisconnect={disconnect}
      onAddAnother={addAnother}
    />
  );
}

/**
 * The "Available to connect" section of the Tools tab — a three-per-row grid of not-yet-connected
 * connectors (Figma 13936-134067; each card is 1/3 of the pane). Owns the apps-list load state
 * (loading / error / unavailable / empty) for the whole Tools tab.
 */
export function AvailableApps({ query, controller }: { query: string; controller: AppsController }) {
  const alfredOnOutreach = useAlfredInOutreachEnabled();
  const { t } = useTranslation();
  const { state } = controller;
  const { askComingSoon } = useAskForConnector();
  useLoadBi('connectors-list', { isLoading: state.kind === 'loading', isError: state.kind === 'error' });
  const searching = query.length > 0;

  if (state.kind === 'loading') {
    return (
      <Section title={t('connectors.apps.available')}>
        <CardGridSkeleton count={6} />
      </Section>
    );
  }
  if (state.kind === 'error') {
    return (
      <Section title={t('connectors.apps.available')}>
        <ErrorLine message={t('connections.error')} />
      </Section>
    );
  }
  if (!state.enabled) {
    return (
      <Section title={t('connectors.apps.available')}>
        <EmptyCard message={t('connectors.apps.unavailableSymphony')} />
      </Section>
    );
  }

  const visible = searching
    ? state.connectors.filter((c) => matchesConnectorQuery(connectorDisplayName(c), c.toolkit, query))
    : state.connectors;
  const available = sortConnectors(visible.filter((c) => !c.connected && listedOnTools(c, alfredOnOutreach)));
  // Drawn by the same card as the real ones, after them: they are meant to read as apps to connect.
  const comingSoon = state.comingSoon
    .map(comingSoonRow)
    .filter((c) => !searching || matchesConnectorQuery(connectorDisplayName(c), c.toolkit, query));
  const count = available.length + comingSoon.length;

  return (
    <Section title={t('connectors.apps.available')} count={count > 0 ? count : undefined}>
      <div className={styles.cardsGrid}>
        {available.map((c) => connectorCard(c, controller, state.multiAccountEnabled))}
        {comingSoon.map((c) => (
          <ConnectorCard
            key={`coming-soon-${c.toolkit}`}
            connector={c}
            busy={NOTHING_BUSY}
            multiAccountEnabled={false}
            onConnect={askComingSoon}
            onDisconnect={() => undefined}
            onAddAnother={() => undefined}
          />
        ))}
        <RequestConnectorCard key={query} initialText={count === 0 ? query : ''} />
      </div>
    </Section>
  );
}

const NOTHING_BUSY: ReadonlySet<string> = new Set();

/**
 * The "Connected" grid — up to four cards per row, full width beneath the genome. Unlike the other Tools
 * grids this one does NOT equalise row heights (`cardsGridConnected`): opening one card's account list
 * grows that card alone and leaves its collapsed neighbours their own size.
 */
export function ConnectedApps({ query, controller }: { query: string; controller: AppsController }) {
  const alfredOnOutreach = useAlfredInOutreachEnabled();
  const { t } = useTranslation();
  const { state, actionError, icloudOpenFor, appleId, applePassword, setAppleId, setApplePassword, submitICloud, busy } =
    controller;
  if (state.kind !== 'ready' || !state.enabled) return null;

  const searching = query.length > 0;
  const visible = searching
    ? state.connectors.filter((c) => matchesConnectorQuery(connectorDisplayName(c), c.toolkit, query))
    : state.connectors;
  const connected = sortConnectors(visible.filter((c) => c.connected && listedOnTools(c, alfredOnOutreach)));

  if (connected.length === 0 && !actionError && !icloudOpenFor) return null;

  return (
    <>
      {actionError && <ErrorLine message={actionError} />}
      {connected.length > 0 && (
        <Section title={t('connectors.apps.connected')} count={connected.length}>
          <div className={`${styles.cardsGrid} ${styles.cardsGridConnected}`}>
            {connected.map((c) => connectorCard(c, controller, state.multiAccountEnabled))}
          </div>
        </Section>
      )}
      {icloudOpenFor && (
        <ICloudForm
          appleId={appleId}
          applePassword={applePassword}
          submitting={busy.has('calendar-apple')}
          onAppleId={setAppleId}
          onApplePassword={setApplePassword}
          onSubmit={submitICloud}
        />
      )}
    </>
  );
}
